import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import {
  fetchAllPermissions,
  editRole,
  fetchAllPermissionsInRole,
  fetchAllRoleAssignmentsInGroup,
} from 'actions/roleActions';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import StylishNewTextArea from 'components/DesignSystems/New/StylishNewTextArea';
import { startLoading } from 'reducers/loading/loading.action';
import { ADMIN_ROLES } from 'constants/group';
import { changeGroupNameToOrganization } from './RolesUtils/groupToOrgTransformations';
import EditRolePermissions from 'components/Roles/EditRolePermissions';

export default function EditRoleDialog({
  show,
  group_guid,
  onClose,
  existingRole,
}) {
  const reduxDispatch = useDispatch();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isValid, isSubmitted },
  } = useForm({ mode: 'onChange' });

  const [isLoaded, setIsLoaded] = useState(true);

  const { user_guid } = useSelector((state) => {
    return state.app.user;
  });

  useEffect(() => {
    if (typeof user_guid !== 'undefined') {
      reduxDispatch(fetchAllPermissions());
      reduxDispatch(fetchAllPermissionsInRole(existingRole));
      reduxDispatch(fetchAllRoleAssignmentsInGroup(existingRole.group_guid));
    }
  }, [user_guid, reduxDispatch, existingRole]);

  const isEditRoleLoaded = useSelector((state) => {
    return state.app.isEditRoleLoaded;
  });

  const isFetchAllOrgScopedRolesInGroupLoaded = useSelector((state) => {
    return state.app.isFetchAllOrgScopedRolesInGroupLoaded;
  });

  useEffect(() => {
    if (
      !isLoaded &&
      isEditRoleLoaded &&
      isFetchAllOrgScopedRolesInGroupLoaded
    ) {
      onClose();
    }
  }, [
    isEditRoleLoaded,
    isFetchAllOrgScopedRolesInGroupLoaded,
    isLoaded,
    onClose,
  ]);

  const reduxAllPermissions = useSelector((state) => {
    const permissions =
      Array.isArray(state.app.allPermissions) &&
      state.app.allPermissions
        .filter((perm) => {
          if (perm.action === 'Open or Close Group') {
            return false;
          }

          return true;
        })
        .map((permission) => {
          switch (permission.action) {
            case 'Open or Close Incident':
              return {
                ...permission,
                title: 'Archive Incident',
                description:
                  'Allows the user to end the activity of a Incident. This locks out all Incident members from performing any action within the Incident or accessing Incident data.',
              };
            default:
              return {
                ...permission,
                title: changeGroupNameToOrganization(permission.title),
                description: changeGroupNameToOrganization(
                  permission.description
                ),
              };
          }
        });

    return permissions || [];
  });

  const reduxAllPermissionsInRole = useSelector((state) => {
    return state.app.allPermissionsInRole;
  });

  useEffect(() => {
    if (typeof reduxAllPermissionsInRole !== 'undefined') {
      setRolePermissions(reduxAllPermissionsInRole);
    }
  }, [reduxAllPermissionsInRole]);

  const [rolePermissions, setRolePermissions] = useState([]);

  useEffect(() => {
    if (existingRole) {
      setValue('roleName', existingRole.name);
      setValue('roleDescription', existingRole.description);
    }
  }, [existingRole, setValue]);

  if (
    typeof reduxAllPermissions === 'undefined' ||
    typeof reduxAllPermissionsInRole === 'undefined'
  ) {
    return <div>Loading Edit Role Dialog....</div>;
  }

  const onEditRoleCreateSubmit = ({ roleName, roleDescription }) => {
    setIsLoaded(false);

    const role = {};
    role['role_id'] = existingRole.id;
    role['group_guid'] = group_guid;
    role['role_name'] = roleName;
    role['description'] = roleDescription;
    role['access_level'] = existingRole.access_level;
    role['permissions'] = rolePermissions.map((perm) => perm.id);
    reduxDispatch(startLoading());
    reduxDispatch(editRole(role));
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        centered
        backdrop="static"
        keyboard={false}
        style={{ '--bs-modal-width': '50vw' }}
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Edit Role</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit(onEditRoleCreateSubmit)}>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Role Name:</label>
              <Controller
                control={control}
                name="roleName"
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <StylishNewInput
                    type="text"
                    onChange={(e) => onChange(e.target.value)}
                    value={value || ''}
                  />
                )}
              />
              {errors.roleName && (
                <span className="form-text form-error">
                  This field is required
                </span>
              )}
              {ADMIN_ROLES.includes(watch('roleName')) ? (
                <span className="form-text form-error">
                  You cannot create a role with that name
                </span>
              ) : null}
            </div>
            <div className="mb-3">
              <label className="form-label">Role Description:</label>
              <Controller
                control={control}
                name="roleDescription"
                rules={{ required: true, maxLength: 500 }}
                render={({ field: { onChange, value } }) => (
                  <StylishNewTextArea
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
              />
              {errors.roleDescription?.type === 'required' && (
                <span className="form-text form-error">
                  This field is required
                </span>
              )}
              {errors.roleDescription?.type === 'maxLength' && (
                <span className="form-text form-error">
                  Maximum 500 character allowed
                </span>
              )}
            </div>
            <hr className="dashed" />
            {!ADMIN_ROLES.includes(watch('roleName')) && (
              <EditRolePermissions
                currentPermissions={rolePermissions}
                onPermissionChange={setRolePermissions}
                permissionsOptions={reduxAllPermissions}
              />
            )}
          </Modal.Body>
          <Modal.Footer className="pt-0">
            <div className="button-group">
              <StylishNewButton
                type="submit"
                className="button--primary"
                disabled={
                  !isLoaded ||
                  ADMIN_ROLES.includes(watch('roleName')) ||
                  (isSubmitted && !isValid)
                }
              >
                Edit Role
              </StylishNewButton>
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
}
